import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MockTestConfig } from "@/types/exam";
import TestHeader from "./TestHeader";
import TestProgressBar from "./TestProgressBar";
import QuestionCard from "./QuestionCard";
import TestNavigation from "./TestNavigation";
import CognitiveInsightsButton from "./CognitiveInsightsButton";
import CognitiveLoadTracker from "./CognitiveLoadTracker";
import { useTestSecurityContext } from "@/context/TestSecurityContext";
import EyeDapticaMessage from "./EyeDapticaMessage";
import { saveMockTestResults } from "@/lib/mockTestResultsUtils";

const MockTest = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [testConfig, setTestConfig] = useState<MockTestConfig | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, number>>({});
  const [flaggedQuestions, setFlaggedQuestions] = useState<Record<string, boolean>>({});
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEyeDapticaIntro, setShowEyeDapticaIntro] = useState(true);

  // Use the TestSecurityContext instead of direct hook
  const { 
    violations, 
    isLocked, 
    isFullscreen,
    enterFullscreen,
    resetViolations,
    unlockTest 
  } = useTestSecurityContext();

  const [questionTimings, setQuestionTimings] = useState<Record<string, number>>({});
  const [navigationPatterns, setNavigationPatterns] = useState<Array<{time: number, from: number, to: number}>>([]);
  const [currentQuestionStartTime, setCurrentQuestionStartTime] = useState<number>(Date.now());
  const [showCognitiveInsights, setShowCognitiveInsights] = useState<boolean>(false);

  useEffect(() => {
    const loadTestConfig = async () => {
      try {
        console.log(`Attempting to load test config for ID: ${testId}`);
        
        const specificKey = `mockTest_${testId}`;
        const storedConfig = localStorage.getItem(specificKey);
        
        console.log(`Looking for test config with key: ${specificKey}`);
        console.log(`Found config? ${Boolean(storedConfig)}`);
        
        if (!storedConfig) {
          const generalConfig = localStorage.getItem('mockTest_config');
          console.log(`Falling back to general config. Found? ${Boolean(generalConfig)}`);
          
          if (!generalConfig) {
            console.error("No test configuration found in localStorage");
            toast.error("Test configuration not found. Please return to the test generator.");
            navigate("/mock-test-generator");
            return;
          }
          
          const parsedConfig = JSON.parse(generalConfig);
          
          console.log(`Loaded test category: ${parsedConfig.category}, exam: ${parsedConfig.exam}`);
          console.log(`Test description: ${parsedConfig.description || 'No description'}`);
          
          localStorage.setItem(specificKey, generalConfig);
          console.log(`Saved general config to specific key: ${specificKey}`);
          
          if (!parsedConfig.questions || !parsedConfig.questions.length) {
            console.error("Invalid test configuration: No questions found");
            toast.error("Invalid test configuration: No questions found");
            navigate("/mock-test-generator");
            return;
          }
          
          console.log(`First few questions in loaded test:`);
          parsedConfig.questions.slice(0, 3).forEach((q, i) => {
            console.log(`Q${i+1} category: ${q.category || 'none'}, subject: ${q.subject || 'none'}`);
            console.log(`Q${i+1} content: ${q.question.substring(0, 50)}...`);
            console.log(`Q${i+1} metadata:`, q.metadata);
          });
          
          if (!parsedConfig.timeLimit || typeof parsedConfig.timeLimit !== 'number') {
            parsedConfig.timeLimit = 60;
            toast.info("Time limit not specified, defaulting to 60 minutes");
          }

          console.log("Loaded test config from general storage:", parsedConfig);
          
          setTestConfig(parsedConfig);
          setTimeRemaining(parsedConfig.timeLimit * 60);
        } else {
          const parsedConfig = JSON.parse(storedConfig);
          
          if (!parsedConfig.questions || !parsedConfig.questions.length) {
            console.error("Invalid test configuration: No questions found");
            toast.error("Invalid test configuration: No questions found");
            navigate("/mock-test-generator");
            return;
          }
          
          console.log(`Test category: ${parsedConfig.category}`);
          console.log(`Test exam type: ${parsedConfig.examType}`);
          console.log(`Test description: ${parsedConfig.description}`);
          console.log(`Number of questions: ${parsedConfig.questions.length}`);
          
          console.log(`First few questions in loaded test:`);
          parsedConfig.questions.slice(0, 3).forEach((q, i) => {
            console.log(`Q${i+1} category: ${q.category || 'none'}, subject: ${q.subject || 'none'}`);
            console.log(`Q${i+1} content: ${q.question.substring(0, 50)}...`);
            console.log(`Q${i+1} metadata:`, q.metadata);
          });
          
          if (!parsedConfig.timeLimit || typeof parsedConfig.timeLimit !== 'number') {
            parsedConfig.timeLimit = 60;
            toast.info("Time limit not specified, defaulting to 60 minutes");
          }

          console.log("Loaded test config from specific storage:", parsedConfig);
          
          setTestConfig(parsedConfig);
          setTimeRemaining(parsedConfig.timeLimit * 60);
        }
        
        // No need for security toast notification here, it's handled by TestSecurityProvider
        setLoading(false);
      } catch (error) {
        console.error("Error loading test:", error);
        toast.error("Failed to load test configuration");
        navigate("/mock-test-generator");
      }
    };
    
    loadTestConfig();
  }, [testId, navigate]);

  useEffect(() => {
    if (!loading && timeRemaining > 0 && !isLocked) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            handleSubmitTest();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [loading, timeRemaining, isLocked]);

  useEffect(() => {
    if (!loading && testConfig && !isLocked) {
      const now = Date.now();
      const timeSpent = Math.round((now - currentQuestionStartTime) / 1000);
      
      if (currentQuestionStartTime !== 0 && timeSpent < 300) {
        setQuestionTimings(prev => ({
          ...prev,
          [currentQuestionIndex]: timeSpent
        }));
      }
      
      if (navigationPatterns.length > 0) {
        const lastNav = navigationPatterns[navigationPatterns.length - 1];
        if (lastNav.to !== currentQuestionIndex) {
          setNavigationPatterns(prev => [
            ...prev,
            {
              time: now,
              from: lastNav.to,
              to: currentQuestionIndex
            }
          ]);
        }
      } else {
        setNavigationPatterns([{
          time: now,
          from: 0,
          to: currentQuestionIndex
        }]);
      }
      
      setCurrentQuestionStartTime(now);
    }
  }, [currentQuestionIndex, loading, testConfig, navigationPatterns, isLocked]);

  const handleSelectAnswer = (questionId: string | number, answerIndex: number) => {
    if (isLocked) return;
    
    const now = Date.now();
    const timeSpent = Math.round((now - currentQuestionStartTime) / 1000);
    
    if (timeSpent < 300) {
      setQuestionTimings(prev => ({
        ...prev,
        [currentQuestionIndex]: timeSpent
      }));
    }
    
    setSelectedAnswers((prev) => ({
      ...prev,
      [questionId]: answerIndex
    }));
    
    setCurrentQuestionStartTime(now);
  };

  const handleFlagQuestion = (questionId: string | number) => {
    if (isLocked) return;
    
    setFlaggedQuestions((prev) => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
    
    toast.success(
      flaggedQuestions[questionId as string] 
        ? "Question unflagged for review" 
        : "Question flagged for review"
    );
  };

  const handlePrevQuestion = () => {
    if (isLocked) return;
    
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleNextQuestion = () => {
    if (isLocked) return;
    
    if (testConfig && currentQuestionIndex < testConfig.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handleSubmitTest = async () => {
    if (!testConfig || isLocked) return;
    
    setIsSubmitting(true);
    
    try {
      let score = 0;
      let totalPossibleScore = 0;
      const questionResults = testConfig.questions.map(question => {
        const userAnswer = selectedAnswers[question.id];
        const correctAnswer = typeof question.correctanswer === 'string' 
          ? parseInt(question.correctanswer, 10) 
          : question.correctanswer;
          
        const isCorrect = userAnswer === correctAnswer;
        
        // Use the marks and negative_marks from each question
        const questionMarks = question.marks || 1; // Default to 1 if not specified
        const questionNegativeMarks = question.negative_marks || 0.25; // Default to 0.25 if not specified
        
        let questionScore = 0;
        if (userAnswer !== undefined) {
          if (isCorrect) {
            questionScore = questionMarks;
            score += questionMarks;
          } else {
            questionScore = -questionNegativeMarks;
            score -= questionNegativeMarks;
          }
        }
        
        totalPossibleScore += questionMarks;
        
        return {
          questionId: question.id,
          userAnswer,
          correctAnswer: correctAnswer,
          isCorrect,
          marks: questionScore
        };
      });
      
      score = Math.max(0, score); // Ensure score doesn't go below 0
      
      const timingValues = Object.values(questionTimings);
      const averageTimePerQuestion = timingValues.length > 0 
        ? timingValues.reduce((sum, time) => sum + time, 0) / timingValues.length 
        : 0;
      
      const cognitiveData = {
        questionTimings,
        navigationPatterns,
        averageTimePerQuestion,
        fatigueZones: Object.entries(questionTimings)
          .filter(([_, time]) => {
            const avgTime = Object.values(questionTimings).reduce((sum, t) => sum + t, 0) / Object.keys(questionTimings).length;
            return time > avgTime * 1.8;
          })
          .map(([questionIndex]) => parseInt(questionIndex)),
        securityViolations: violations.total
      };
      
      const results = {
        id: `result_${Date.now()}`,
        testId,
        testConfig,
        submittedAnswers: selectedAnswers,
        score,
        totalPossibleScore,
        percentage: Math.round((score / totalPossibleScore) * 100),
        questionResults,
        timeSpent: testConfig.timeLimit * 60 - timeRemaining,
        completedAt: new Date().toISOString(),
        cognitiveData,
        securityViolations: violations.total,
        wasLocked: isLocked
      };
      
      // Save the results to both localStorage and the database
      await saveMockTestResults(results);
      
      // Use the context's resetViolations and unlockTest
      resetViolations();
      unlockTest();
      
      navigate(`/mock-test-results/${results.id}`);
    } catch (error) {
      console.error("Error submitting test:", error);
      toast.error("Failed to submit test");
      setIsSubmitting(false);
    }
  };

  const toggleCognitiveInsights = () => {
    if (isLocked) return;
    setShowCognitiveInsights(prev => !prev);
  };

  useEffect(() => {
    if (loading) return;
    setShowEyeDapticaIntro(true);
    const timer = setTimeout(() => {
      setShowEyeDapticaIntro(false);
    }, 10000); // 10s

    return () => clearTimeout(timer);
  }, [loading, testId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-100px)]">
        <span className="loading loading-spinner loading-lg text-primary"></span>
      </div>
    );
  }

  if (!testConfig) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-100px)]">
        <AlertTriangle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-2xl font-bold mb-2">Test Not Found</h2>
        <p className="text-gray-600 mb-4">The test configuration could not be loaded.</p>
        <Button onClick={() => navigate("/mock-test-generator")}>
          Back to Test Generator
        </Button>
      </div>
    );
  }

  if (!testConfig.questions || testConfig.questions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-100px)]">
        <AlertTriangle className="h-12 w-12 text-amber-500 mb-4" />
        <h2 className="text-2xl font-bold mb-2">No Questions Available</h2>
        <p className="text-gray-600 mb-4">This mock test has no questions. Please generate a new test.</p>
        <Button onClick={() => navigate("/mock-test-generator")}>
          Back to Test Generator
        </Button>
      </div>
    );
  }

  const currentQuestion = testConfig.questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === testConfig.questions.length - 1;

  let testTitle = "";

  if (testConfig.testTitle) {
    testTitle = testConfig.testTitle;
  } else if (testConfig.description) {
    testTitle = testConfig.description;
  } else if (testConfig.category && testConfig.category !== "IIT JEE") {
    testTitle = testConfig.category;
  } else if (testConfig.exam) {
    testTitle = testConfig.exam;
  } else if (testConfig.sections && testConfig.sections.length > 0) {
    testTitle = `${testConfig.sections[0].name} Test`;
  } else {
    testTitle = "Mock Test";
  }

  console.log(`Using test title: "${testTitle}", derived from: testTitle=${testConfig.testTitle}, description=${testConfig.description}, category=${testConfig.category}, exam=${testConfig.exam}`);

  if (testConfig.description) {
    console.log("Full test description:", testConfig.description);
  }

  if (!currentQuestion || typeof currentQuestion.question !== 'string' || !Array.isArray(currentQuestion.options)) {
    console.error("Invalid question format:", currentQuestion);
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-100px)]">
        <AlertTriangle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-2xl font-bold mb-2">Invalid Question Format</h2>
        <p className="text-gray-600 mb-4">The questions in this test have an invalid format.</p>
        <Button onClick={() => navigate("/mock-test-generator")}>
          Back to Test Generator
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-4 max-w-4xl relative">
      {showEyeDapticaIntro && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[2500] flex items-center justify-center transition-all duration-500">
          <EyeDapticaMessage mode="full" />
        </div>
      )}

      {!showEyeDapticaIntro && (
        <EyeDapticaMessage mode="avatar" />
      )}

      <div className={showEyeDapticaIntro ? "pointer-events-none opacity-40 select-none transition-all duration-700" : "transition-all duration-700"}>
        <TestHeader
          testTitle={testTitle}
          questionCount={testConfig.questions.length}
          timeLimit={testConfig.timeLimit}
          timeRemaining={timeRemaining}
          isSubmitting={isSubmitting}
          onSubmitTest={handleSubmitTest}
          violations={violations.total}
          isLocked={isLocked}
        />

        <TestProgressBar
          currentQuestionIndex={currentQuestionIndex}
          totalQuestions={testConfig.questions.length}
        />

        {!isLocked && (
          <CognitiveInsightsButton
            showInsights={showCognitiveInsights}
            hasEnoughData={Object.keys(questionTimings).length > 5}
            onToggle={toggleCognitiveInsights}
          />
        )}

        {showCognitiveInsights && Object.keys(questionTimings).length > 5 && !isLocked && (
          <CognitiveLoadTracker
            questionTimings={questionTimings}
            navigationPatterns={navigationPatterns}
            currentQuestionIndex={currentQuestionIndex}
            totalQuestions={testConfig.questions.length}
          />
        )}

        <QuestionCard
          question={currentQuestion}
          currentQuestionIndex={currentQuestionIndex}
          selectedAnswer={selectedAnswers[currentQuestion.id]}
          onSelectAnswer={handleSelectAnswer}
          flaggedQuestions={flaggedQuestions}
          onFlagQuestion={handleFlagQuestion}
          isLocked={isLocked}
        />

        <TestNavigation
          isLastQuestion={isLastQuestion}
          currentQuestionIndex={currentQuestionIndex}
          onPrevious={handlePrevQuestion}
          onNext={handleNextQuestion}
          onSubmit={handleSubmitTest}
          isSubmitting={isSubmitting}
          isLocked={isLocked}
          totalQuestions={testConfig.questions.length}
          currentIndex={currentQuestionIndex}
          onNavigate={setCurrentQuestionIndex}
          answers={selectedAnswers}
          flaggedQuestions={flaggedQuestions}
        />
      </div>
    </div>
  );
};

export default MockTest;
